import DropDownList from "@/components/Dropdown";
import { EnumStatus } from "@/constants/enum";
import { useLazyGetDropDownBusinessUnitsQuery } from "@/services/businessunit.service";
import { HouseRentalForm } from "@/types/house-rental.type";
import { moneyRegex } from "@/utils/regex";
import _ from "lodash";
import moment from "moment-timezone";
import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";


export default function Information() {
    const {t} = useTranslation()
    const navigate = useNavigate();
    const {state: {business_unit_id, id}} = useLocation()
    const [getBusinessUnits, {data: businessUnits}] = useLazyGetDropDownBusinessUnitsQuery({refetchOnFocus: true, refetchOnReconnect: true})
    const {register, formState: { errors }, control, setValue, watch} = useFormContext<HouseRentalForm>()
    const taStart = watch("ta_start")
    const taDuration = watch("ta_duration")

    useEffect(() => {
        getBusinessUnits({status: EnumStatus.ACTIVE.toString(), room: true, checkExpired: false})
    }, [business_unit_id])


    useEffect(() => {
        if(businessUnits && businessUnits?.length > 0 && business_unit_id) {
            setValue("business_unit_id", business_unit_id)
        }
    }, [businessUnits, business_unit_id])

    useEffect(() => {
        if(taStart && taDuration) {
            setValue("ta_end", moment(taStart, "YYYY-MM-DD").add(taDuration, "months").add(-1, 'day').format("YYYY-MM-DD"))
        }
    }, [taStart, taDuration])

    return (
        <div className="grid grid-cols-1 gap-9 sm:grid-cols-2 overflow-x-hidden overflow-y-auto px-6">
            <div>
                <div className="mb-3">
                    <label className="mb-1 block dark:text-white">
                        {t("house_rental.business_unit")}
                    </label>
                    <div className="flex flex-wrap">
                        <Controller
                            name="business_unit_id"
                            control={control}
                            render={({field: {onChange, value}}) => <div className="grow">
                                <DropDownList selected={value} setSelected={onChange} list={businessUnits || []} />
                            </div>}
                        />
                        <button type="button" className="rounded bg-primary ms-3 p-1.5 px-3 text-white hover:bg-opacity-90" onClick={() => navigate(location, { state: { modal: "/settings/business-units/add", fromModal: id ? "/house-rentals/edit" : "/house-rentals/add", id: id } })}>Add</button>
                   </div>
                </div>
                <div className="mb-3">
                    <label className="mb-1 block text-black dark:text-white">
                        {t("house_rental.rent")}
                    </label>
                    <input
                        type="text"
                        placeholder={t("placeholder.rent")}
                        className="w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary dark:text-white"
                        {...register("rent", {
                            required: t("validation.required"),
                            pattern: {
                                value: moneyRegex,
                                message: t("validation.incorrect_money"),
                            }
                        })}
                    />
                    { errors.rent && <p className="mt-1 text-rose-600">{errors.rent.message}</p>}
                </div>
                <div className="mb-3">
                    <label className="mb-1 block text-black dark:text-white">
                        {t("house_rental.deposit_amount")}
                    </label>
                    <input
                        type="text"
                        placeholder={t("placeholder.deposit_amount")}
                        className="w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary dark:text-white"
                        {...register("deposit_amount", {
                            required: t("validation.required"),
                            pattern: {
                                value: moneyRegex,
                                message: t("validation.incorrect_money"),
                            }
                        })}
                    />
                    { errors.deposit_amount && <p className="mt-1 text-rose-600">{errors.deposit_amount.message}</p>}
                </div>
                <div className="mb-3">
                    <label className="mb-1 block text-black dark:text-white">
                        {t("house_rental.rent_free")}
                    </label>
                    <input
                        type="number"
                        placeholder={t("placeholder.rent_free")}
                        className="w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary dark:text-white"
                        {...register("rent_free", {
                            min: {
                                value: 0,
                                message: t("validation.min_0"),
                            },
                        })}
                    />
                    { errors.rent_free && <p className="mt-1 text-rose-600">{errors.rent_free.message}</p>}
                </div>
                <div className="mb-3">
                    <label className="mb-1 block text-black dark:text-white">
                        {t("house_rental.warranty")}
                    </label>
                    <input
                        type="number"
                        placeholder={t("placeholder.warranty")}
                        className="w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary dark:text-white"
                        {...register("warranty", {
                            min: {
                                value: 0,
                                message: t("validation.min_0"),
                            },
                        })}
                    />
                    { errors.warranty && <p className="mt-1 text-rose-600">{errors.warranty.message}</p>}
                </div>
                <div className="mb-3">
                    <label className="mb-1 block text-black dark:text-white">
                        {t("house_rental.agent_name")}
                    </label>
                    <input
                        type="text"
                        placeholder={t("placeholder.agent_name")}
                        className="w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:text-white dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                        {...register("agent_name", {required: t("validation.required")})}
                    />
                    { errors.agent_name && <p className="mt-1 text-rose-600">{errors.agent_name.message}</p>}
                </div>
            </div>
            <div>
                <div className="mb-3">
                    <label className="mb-1 block text-black dark:text-white">
                        {t("house_rental.ta_start")}
                    </label>
                    <input
                        type="date"
                        placeholder={t("placeholder.business_unit_account")}
                        className="custom-input-date-1 w-full rounded border-[1.5px] text-black dark:text-white border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary disabled:opacity-75"
                        {...register("ta_start", {
                            required: t("validation.required")
                        })}
                    />
                    { errors.ta_start && <p className="mt-1 text-rose-600">{errors.ta_start.message}</p>}
                </div>
                <div className="mb-3">
                    <label className="mb-1 block text-black dark:text-white">
                        {t("house_rental.ta_duration")}
                    </label>
                    <input
                        type="number"
                        placeholder={t("placeholder.ta_duration")}
                        className="w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary dark:text-white"
                        {...register("ta_duration", {
                            required: t("validation.required"),
                            min: {
                                value: 1,
                                message: t("validation.min_1"),
                            },
                        })}
                    />
                    { errors.ta_duration && <p className="mt-1 text-rose-600">{errors.ta_duration.message}</p>}
                </div>
                <div className="mb-3">
                    <label className="mb-1 block text-black dark:text-white">
                        {t("house_rental.ta_end")}
                    </label>
                    <input
                        type="date"
                        disabled={true}
                        placeholder={t("placeholder.business_unit_account")}
                        className="custom-input-date-1 w-full rounded border-[1.5px] text-black dark:text-white border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary disabled:bg-gray-200"
                        {...register("ta_end", {
                            required: t("validation.required")
                        })}
                    />
                    { errors.ta_start && <p className="mt-1 text-rose-600">{errors.ta_start.message}</p>}
                </div>
                <div className="mb-3">
                    <label className="mb-1 block text-black dark:text-white">
                        {t("house_rental.take_over")}
                    </label>
                    <input
                        type="date"
                        placeholder={t("placeholder.business_unit_account")}
                        className="custom-input-date-1 w-full rounded border-[1.5px] text-black dark:text-white border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary disabled:opacity-75"
                        {...register("take_over", {required: t("validation.required")})}
                    />
                    { errors.take_over && <p className="mt-1 text-rose-600">{errors.take_over.message}</p>}
                </div>
                <div className="mb-3">
                    <label className="mb-1 block text-black dark:text-white">
                        {t("house_rental.option_to_renew")}
                    </label>
                    <input
                        type="text"
                        placeholder={t("placeholder.option_to_renew")}
                        className="w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary dark:text-white"
                        {...register("option_to_renew", {
                            pattern: {
                                value: /^[1-9][0-9]*$/,
                                message: t("validation.number_valid"),
                            },
                            min: {
                                value: 1,
                                message: t("validation.min_1"),
                            },
                        })}
                    />
                    { errors.option_to_renew && <p className="mt-1 text-rose-600">{errors.option_to_renew.message}</p>}
                </div>
            </div>
        </div>
    )
}
